import { useNavigate } from 'react-router-dom';
import { Check, Clock } from 'lucide-react';
import styles from './ZeroState.module.css';

type Step = {
  num: number;
  colorClass: string;
  title: string;
  desc: string;
  time: string;
  cta: string;
  ctaHref: string;
  unlocks: string[];
};

const STEPS: Step[] = [
  {
    num: 1,
    colorClass: styles.accent,
    title: 'Create your growth strategy',
    desc: 'Answer a few questions about your practice. Our AI analyzes your market, competitors, and demographics to build a custom strategy.',
    time: '~8 min',
    cta: 'Create Strategy →',
    ctaHref: '/strategy/builder',
    unlocks: [
      'Competitor analysis',
      'Market gap insights',
      'Recommended ad budget',
      'Targeted patient segments',
    ],
  },
  {
    num: 2,
    colorClass: styles.blue,
    title: 'Connect your ad accounts',
    desc: "Link Google Ads and Meta to see campaigns, spend, and conversions in one place. We'll run an instant AI audit on your existing campaigns.",
    time: '~2 min',
    cta: 'Connect Ads →',
    ctaHref: '/settings/paid',
    unlocks: [
      'Total spend & conversions',
      'Campaign performance',
      'AI optimization recommendations',
      'Cross-platform view',
    ],
  },
  {
    num: 3,
    colorClass: styles.purple,
    title: 'Connect visibility accounts',
    desc: 'Link Google Search Console and Google Business Profile to track how patients find you online and monitor your reviews automatically.',
    time: '~3 min',
    cta: 'Connect Accounts →',
    ctaHref: '/settings/organic',
    unlocks: [
      'Google search clicks',
      'Review monitoring & auto-response',
      'Online Visibility Score',
      'AI Search Readiness',
    ],
  },
];

export function ZeroState({ strategyInProgress = false }: { strategyInProgress?: boolean }) {
  const navigate = useNavigate();
  return (
    <div>
      <div className={styles.hero}>
        <div className={styles.eyebrow}>Welcome to PracticeNova</div>
        <div className={styles.headline}>Set up your AI growth dashboard</div>
        <div className={styles.subtext}>
          Three steps to unlock your full dashboard. Each can be done independently — start
          anywhere.
        </div>
      </div>

      <div className={styles.steps}>
        {STEPS.map((s) => (
          <div key={s.num} className={styles.stepCard}>
            <div className={styles.stepLayout}>
              <div className={`${styles.stripe} ${s.colorClass}`} />
              <div className={styles.stepBody}>
                <div className={`${styles.stepNum} ${s.colorClass}`}>
                  <span>{s.num}</span>
                </div>
                <div className={styles.stepContent}>
                  <div className={styles.stepHeader}>
                    <div className={styles.stepTitle}>{s.title}</div>
                    <div className={styles.stepTime}>
                      <Clock size={12} aria-hidden /> {s.time}
                    </div>
                  </div>
                  <div className={styles.stepDesc}>{s.desc}</div>
                  <div className={styles.stepActions}>
                    <button
                      type="button"
                      onClick={() => navigate(s.ctaHref)}
                      className={`${styles.stepCta} ${s.colorClass}`}
                    >
                      {s.num === 1 && strategyInProgress ? 'Complete Strategy →' : s.cta}
                    </button>
                    <div className={styles.unlocks}>
                      {s.unlocks.map((u) => (
                        <span key={u} className={`${styles.unlockPill} ${s.colorClass}`}>
                          <Check size={12} aria-hidden /> {u}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
